import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar, MapPin, Clock, Users, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/utils/languageRouter";

const UpcomingDepartures = ({ departures }) => {
  const { i18n } = useTranslation();
  const { lang: urlLang } = useParams();
  const lang = urlLang || i18n.language;
  const currentLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  const localizedLink = (path) => {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${currentLang}${cleanPath}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getDaysUntil = (dateStr) => {
    const today = new Date();
    const departureDate = new Date(dateStr);
    const diffTime = departureDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const texts = {
    title: {
      fr: "Prochains Départs",
      en: "Upcoming Departures",
      ar: "المغادرات القادمة",
      nl: "Aankomende Vertrekken",
      es: "Próximas Salidas",
    },
    subtitle: {
      fr: "Rejoignez-nous",
      en: "Join Us",
      ar: "انضم إلينا",
      nl: "Doe mee",
      es: "Únete a nosotros",
    },
    spots: {
      fr: "places",
      en: "spots",
      ar: "أماكن",
      nl: "plaatsen",
      es: "plazas",
    },
    days: {
      fr: "jours",
      en: "days",
      ar: "أيام",
      nl: "dagen",
      es: "días",
    },
    daysUntil: {
      fr: "Dans",
      en: "In",
      ar: "في",
      nl: "Over",
      es: "En",
    },
    book: {
      fr: "Réserver",
      en: "Book Now",
      ar: "احجز الآن",
      nl: "Boek nu",
      es: "Reservar",
    },
    viewAll: {
      fr: "Voir toutes les dates",
      en: "View all dates",
      ar: "عرض جميع التواريخ",
      nl: "Bekijk alle data",
      es: "Ver todas las fechas",
    },
  };

  if (!departures || departures.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="text-primary font-medium tracking-widest uppercase text-sm">
            {texts.subtitle[lang] || texts.subtitle.fr}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold mt-2 text-secondary">
            {texts.title[lang] || texts.title.fr}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {departures.map((item, index) => {
            const { trip_date, circuit } = item;
            const daysUntil = getDaysUntil(trip_date.start_date);
            const spotsLeft = trip_date.available_spots - (trip_date.booked_spots || 0);

            return (
              <div
                key={trip_date.id || index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group"
                data-testid={`departure-card-${index}`}
              >
                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={circuit.image_url || "https://images.unsplash.com/photo-1682686581556-a3f0ee0ed556"}
                    alt={circuit.title?.[lang] || circuit.title?.fr}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  {/* Countdown Badge */}
                  {daysUntil > 0 && daysUntil <= 30 && (
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-primary text-white font-bold px-3 py-1">
                        {texts.daysUntil[lang] || texts.daysUntil.fr} {daysUntil} {texts.days[lang] || texts.days.fr}
                      </Badge>
                    </div>
                  )}
                  
                  {/* Type Badge */}
                  <div className="absolute top-3 left-3">
                    <Badge className={circuit.type === "4x4" ? "bg-amber-600" : "bg-emerald-600"}>
                      {circuit.type === "4x4" ? "4x4" : "Trek"}
                    </Badge>
                  </div>
                  
                  {/* Title overlay */}
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="font-serif text-lg font-bold text-white line-clamp-1">
                      {circuit.title?.[lang] || circuit.title?.fr}
                    </h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  {/* Date */}
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-secondary">
                      {formatDate(trip_date.start_date)}
                    </span>
                    <span className="text-muted-foreground">→</span>
                    <span className="text-muted-foreground">
                      {formatDate(trip_date.end_date)}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="flex flex-wrap gap-3 mb-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {circuit.departure_city}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {circuit.duration_days} {texts.days[lang] || texts.days.fr}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {spotsLeft} {texts.spots[lang] || texts.spots.fr}
                    </span>
                  </div>

                  {/* Price & CTA */}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div>
                      <span className="text-2xl font-bold text-primary">
                        {trip_date.price_override || circuit.price}€
                      </span>
                      <span className="text-sm text-muted-foreground">/pers</span>
                    </div>
                    <Link to={localizedLink(`/circuit/${circuit.id}`)}>
                      <Button size="sm" className="rounded-full">
                        {texts.book[lang] || texts.book.fr}
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Link to={localizedLink("/circuits")}>
            <Button variant="outline" size="lg" className="rounded-full">
              {texts.viewAll[lang] || texts.viewAll.fr}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default UpcomingDepartures;
